import {
	Button,
	ClickAwayListener,
	Divider,
	Grid,
	List,
	Typography,
} from '@mui/material';
import { Stack } from '@mui/system';
import { useMemo, useState } from 'react';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useBoundStore } from 'useStore';
import useYearsData from 'views/Components/Forecast/Queries/yearsQuery';
import LightTooltip from 'views/Components/Shared/Components/LightTooltip';

const LIMIT = 12;

function Options({ closeTooltip, years }) {
	const selectedYear = useBoundStore((state) => state.fhSelectedYear);
	const setYear = useBoundStore((state) => state.setFhSelectedYear);

	const totalItems = years.length;
	const [page, setPage] = useState(2);
	const offset = (page - 1) * LIMIT;
	const totalPages = Math.ceil(totalItems / LIMIT);
	const paginatedYears = useMemo(
		() => years.slice(offset, offset + LIMIT),
		[offset, years]
	);

	const handleYearChange = (year) => {
		setYear(year);
	};

	return (
		<List
			sx={{
				width: '340px',
				maxWidth: 340,
				bgcolor: 'background.paper',
				padding: '8px',
			}}
			component="nav"
			aria-labelledby="nested-list-subheader"
			subheader={
				<Typography
					sx={{
						color: 'rgba(0, 0, 0, 0.87)',
						fontFamily: 'Roboto',
						fontStyle: 'normal',
						fontSize: '16px',
						lineHeight: '24px',
						fontWeight: '700',
						letterSpacing: '0.15px',
						paddingY: 1,
						textAlign: 'center',
					}}
				>
					Elegir Año
				</Typography>
			}
		>
			<Divider />

			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				sx={{
					pt: '8px',
				}}
			>
				<Typography
					sx={{
						color: 'rgba(0, 0, 0, 0.87)',
						fontFamily: 'Roboto',
						fontStyle: 'normal',
						fontSize: '16px',
						lineHeight: '24px',
						fontWeight: '700',
						letterSpacing: '0.15px',
						paddingY: 1,
						textAlign: 'center',
					}}
				>
					Año {selectedYear}
				</Typography>

				<Stack direction="row" alignItems="center" justifyContent="center">
					<Button
						onClick={() => setPage((prev) => prev - 1)}
						disabled={page === 1}
						sx={{
							minWidth: '0px',
						}}
					>
						<KeyboardArrowLeftIcon
							sx={{
								color: page === 1 ? '#00000061' : '#0000008F',
							}}
						/>
					</Button>
					<Button
						onClick={() => setPage((prev) => prev + 1)}
						disabled={page === totalPages}
						sx={{
							minWidth: '0px',
						}}
					>
						<KeyboardArrowRightIcon
							sx={{
								color: page === totalPages ? '#00000061' : '#0000008F',
							}}
						/>
					</Button>
				</Stack>
			</Stack>

			<Grid
				container
				spacing={2}
				sx={{
					paddingY: 2,
				}}
			>
				{paginatedYears.map((year) => (
					<Grid item xs={4} key={year.id}>
						<Button
							variant={selectedYear === year.value ? 'contained' : 'outlined'}
							sx={{
								borderRadius: '100px',
								border: 'none',
								':hover': {
									border: 'none',
									backgroundColor:
										selectedYear === year.value ? '#3F51B5' : '#3F51B54d',
								},
								backgroundColor:
									selectedYear === year.value ? '#3F51B5' : 'transparent',
								color: selectedYear === year.value ? '#FFFFFF' : '#000000DE',
							}}
							onClick={() => handleYearChange(year.value)}
							fullWidth
						>
							{year.value}
						</Button>
					</Grid>
				))}
			</Grid>

			<Divider />

			<Stack
				direction="row"
				spacing={1}
				justifyContent="flex-end"
				alignItems="center"
				sx={{
					pt: 1,
				}}
			>
				<Button
					color="secondary"
					onClick={closeTooltip}
					sx={{
						color: '#3F51B5',
					}}
				>
					Cerrar
				</Button>
			</Stack>
		</List>
	);
}

export default function HistoryYearPicker() {
	const [open, setOpen] = useState(false);
	const yearsQuery = useYearsData();
	const selectedYear = useBoundStore((state) => state.fhSelectedYear);

	const handleTooltipClose = () => {
		setOpen(false);
	};

	const handleToggleTooltip = () => {
		setOpen((prev) => !prev);
	};

	return (
		<ClickAwayListener onClickAway={handleTooltipClose}>
			<div>
				<LightTooltip
					PopperProps={{
						disablePortal: true,
					}}
					onClose={handleTooltipClose}
					open={open}
					disableFocusListener
					disableHoverListener
					disableTouchListener
					title={
						<Options
							closeTooltip={handleTooltipClose}
							years={yearsQuery?.data}
						/>
					}
				>
					<Stack
						direction="row"
						alignItems="center"
						justifyContent="space-between"
						sx={{
							padding: '12px',
							width: '182px',
							borderRadius: '4px',
							border: '1px solid rgba(0, 0, 0, 0.23)',
							cursor: 'pointer',
						}}
						onClick={handleToggleTooltip}
					>
						<Typography
							sx={{
								color: '#00000099',
								fontSize: '16px',
								fontFamily: 'Roboto',
								fontStyle: 'normal',
								fontWeight: '400',
								lineHeight: '24px',
								letterSpacing: '0.15px',
							}}
						>
							Año {selectedYear}
						</Typography>
						<CalendarTodayOutlinedIcon
							sx={{
								color: '#0000008F',
								width: '24px',
								height: '24px',
							}}
						/>
					</Stack>
				</LightTooltip>
			</div>
		</ClickAwayListener>
	);
}
